import { useState, useEffect } from 'react';

// Maps ISO 3166-1 alpha-2 country codes to flag emojis
const getFlagEmoji = (countryCode) => {
  if (!countryCode || countryCode.length !== 2) return '🌍';
  const codePoints = countryCode
    .toUpperCase()
    .split('')
    .map(char => 127397 + char.charCodeAt(0));
  return String.fromCodePoint(...codePoints);
};

// Maps country codes to suggested currencies
const COUNTRY_CURRENCY_MAP = {
  US: 'USD', GB: 'GBP', AU: 'AUD', CA: 'CAD',
  NG: 'NGN', DE: 'EUR', FR: 'EUR', ES: 'EUR',
  IT: 'EUR', NL: 'EUR', BE: 'EUR', PT: 'EUR',
  AT: 'EUR', IE: 'EUR', FI: 'EUR', GR: 'EUR',
  CM: 'CAF', SN: 'CAF', CI: 'CAF',
};

const CACHE_KEY = 'ncb_geo_cache';
const CACHE_TTL = 1000 * 60 * 60 * 6; // 6 hours

export const useCountryFlag = () => {
  const [countryCode, setCountryCode] = useState(null);
  const [flagEmoji, setFlagEmoji] = useState('🌍');
  const [countryName, setCountryName] = useState('');
  const [suggestedCurrency, setSuggestedCurrency] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const cached = localStorage.getItem(CACHE_KEY);
    if (cached) {
      try {
        const { data, timestamp } = JSON.parse(cached);
        if (Date.now() - timestamp < CACHE_TTL) {
          setCountryCode(data.country_code);
          setFlagEmoji(getFlagEmoji(data.country_code));
          setCountryName(data.country_name);
          setSuggestedCurrency(COUNTRY_CURRENCY_MAP[data.country_code] || null);
          setLoading(false);
          return;
        }
      } catch (_) {}
    }

    const controller = new AbortController();

    fetch('https://ipapi.co/json/', { signal: controller.signal })
      .then(res => res.json())
      .then(data => {
        if (data && data.country_code) {
          const code = data.country_code;
          const name = data.country_name || '';
          localStorage.setItem(CACHE_KEY, JSON.stringify({ data: { country_code: code, country_name: name }, timestamp: Date.now() }));
          setCountryCode(code);
          setFlagEmoji(getFlagEmoji(code));
          setCountryName(name);
          setSuggestedCurrency(COUNTRY_CURRENCY_MAP[code] || null);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));

    return () => controller.abort();
  }, []);

  return { countryCode, flagEmoji, countryName, suggestedCurrency, loading };
};